import './style.modules.scss'

import React, { useEffect, useId, useState } from 'react'
import xss from 'xss'

import { useAmpUserStatus } from '../../hooks/useAmpUserStatus'
import { getMenu } from '../../lib/CfgApi/getMenu'
import { MenuType } from '../../lib/CfgApi/menu-type.type'
import { SiteNavAnalytics } from '../../lib/helpers/SiteNavAnalytics'
import { Link } from '../../types/Link.type'
import { UserStatus } from '../../types/UserStatus.enum'
import { useFooterPrivacyLink } from './useFooterPrivacyLink'

export type FooterNavigationProps = {
  menuName?: string
  version?: string
  customerServiceHeading: string
  customerServiceDescription: string
  customerServiceLink: string
  alwaysShowOptOutLink?: boolean
  testId?: string
}

export const FooterNavigation = (props: FooterNavigationProps) => {
  const {
    menuName,
    version,
    customerServiceHeading,
    customerServiceDescription,
    customerServiceLink,
    alwaysShowOptOutLink = false,
    testId
  } = props
  const [menuItems, setMenuItems] = useState<Link[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const customerServiceHeadingId = useId()
  const showPrivacyLink = useFooterPrivacyLink({ alwaysShowOptOutLink })
  const host = window.location.hostname
  const isAmeripriseAdvisorsSite =
    /www\.qa\.ameripriseadvisors\.com/.test(host) ||
    /www\.ameripriseadvisors\.com/.test(host)
  const userStatus = useAmpUserStatus()

  useEffect(() => {
    // TODO move fetching out of presentation component
    function getMenuType(): MenuType {
      if (menuName) {
        return menuName as MenuType
      }
      if (userStatus === UserStatus.CLIENT_ACTIVE) {
        return 'footer-menu-client-active'
      }
      return 'footer-menu-prospect'
    }

    const fetchData = async () => {
      setIsLoading(true)

      const menuType = getMenuType()
      const navMenuItems = await getMenu(menuType, 1, version ?? '0')
      setMenuItems(navMenuItems)
      setIsLoading(false)
    }

    fetchData()
  }, [menuName, version, userStatus])

  function handleClick(event: React.MouseEvent<HTMLAnchorElement>) {
    try {
      const footerAnalytics = new SiteNavAnalytics()
      footerAnalytics.ping(event.currentTarget.text, 'footer')
    } catch {
      console.log('Footer analytics ping failed')
    }
  }

  return (
    <div className="u-adel-reset FooterNavigation u-pt-hidden u-block">
      <div className="u-sizeConstrained u-paddingVert32 u-bgColorWhite">
        <div className="Grid Grid--fill Grid--withGutter">
          <div className="u-sizeFill u-lg-size2of3">
            <nav role="navigation">
              {isLoading && (
                <div className="Loading u-textColorBrandLight">
                  <div className="Loading-indicator"></div>
                  <div className="Loading-indicator"></div>
                  <div className="Loading-indicator"></div>
                </div>
              )}
              {!isLoading && (
                <ul className="u-decoUnbulleted" data-qa="FooterNavLevelOne">
                  {menuItems?.map((menuItem, index) =>
                    menuItem.name !== 'Do Not Sell or Share My Personal Data' ||
                    showPrivacyLink ? (
                      <li
                        key={index}
                        className={
                          'u-marginBottom32' +
                          (menuItem.name === 'Customer Service'
                            ? ' u-lg-hidden'
                            : '')
                        }
                      >
                        <a
                          onClick={handleClick}
                          className="Link u-textBold u-textColorDefault"
                          href={
                            isAmeripriseAdvisorsSite &&
                            menuItem.name ===
                              'Do Not Sell or Share My Personal Data'
                              ? `/cookie-opt-out` // CPRA page on Advisor site
                              : xss(menuItem.url)
                          }
                        >
                          {xss(menuItem.name)}
                        </a>
                      </li>
                    ) : (
                      ''
                    )
                  )}
                </ul>
              )}
            </nav>
          </div>
          <div className="u-sm-hidden u-md-hidden u-lg-size1of3">
            <div className="u-posRelative">
              {/* eslint-disable-next-line jsx-a11y/anchor-has-content -- named via aria-labelledby on the visible heading */}
              <a
                className="Link--stretched"
                href={customerServiceLink}
                aria-labelledby={customerServiceHeadingId}
                data-testid={
                  testId ? `${testId}-customer-service-link` : undefined
                }
              />
              <span
                id={customerServiceHeadingId}
                className="u-textPrimary u-textBold u-borderBottom u-borderColorNeutralLight u-paddingBottom8 u-marginBottom8 u-block"
                data-testid={
                  testId ? `${testId}-customer-service-heading` : undefined
                }
              >
                {customerServiceHeading}
              </span>
              <p
                className="u-marginBottom32 u-textColorLight"
                data-testid={
                  testId ? `${testId}-customer-service-body` : undefined
                }
              >
                {customerServiceDescription}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
